import { isApiTokenMessage, isForbiddenMessage } from './error-copy';
/** 把任何丟出來的東西轉成 HTTP 狀態（沒有就 null）。 */
export function errorStatusOf(err) {
    const status = err?.status;
    return typeof status === 'number' ? status : null;
}
/** fetch 層「連不上」的字樣（http-client 會包成「連不到 Worker（GET …）」）。 */
const AUTH_PATTERN = /\b40[13]\b|unauthenticated|unauthorized|forbidden|未授權|沒有權限|請重新登入|登入已失效|操作金鑰|需要操作金鑰|x-api-token/i;
const OFFLINE_PATTERN = /連不到|連不上|無法連線|Failed to fetch|NetworkError|network\s*error|ERR_NETWORK|ERR_CONNECTION|ETIMEDOUT|ECONNREFUSED|ECONNRESET|ENOTFOUND|timeout|timed?\s*out|aborted|載入失敗.*fetch/i;
/** 這個錯誤屬於哪一種？（順序：狀態碼優先，再看字樣，最後才是其他） */
export function errorKindOf(err, statusFallback = null) {
    const status = errorStatusOf(err) ?? statusFallback;
    if (status === 401 || status === 403)
        return 'unauthorized';
    if (status !== null && status >= 500)
        return 'offline';
    const message = err instanceof Error ? err.message : typeof err === 'string' ? err : '';
    // 只剩字串時（`useInbox.error` 就是字串）狀態碼已遺失，靠字樣仍要分得出 401／403。
    if (AUTH_PATTERN.test(message))
        return 'unauthorized';
    if (OFFLINE_PATTERN.test(message))
        return 'offline';
    // 沒有狀態碼、也不像網路問題（解析失敗、前端自丟的「這個 ApiClient 沒有 X」）→ 一般錯誤。
    return 'error';
}
/** 四態判定的唯一入口；純函式，測試與 UI 共用同一份規則。 */
export function resolveDataState(input) {
    const { loading = false, error = null, errorKind = null, count, loaded = true } = input;
    const hasError = typeof error === 'string' && error.trim() !== '';
    if (hasError)
        return errorKind ?? errorKindOf(error);
    if (loading && count === 0)
        return 'loading';
    if (!loaded && count === 0)
        return 'loading';
    if (count === 0)
        return 'empty';
    return 'ready';
}
/** 每一態的預設文案；呼叫端可以覆寫，但不要讓「錯誤」說成「空」。 */
export const DATA_STATE_COPY = {
    loading: { title: '讀取中…' },
    empty: { title: '還沒有資料' },
    offline: {
        title: '連不上後端',
        hint: '網路斷了或 Worker 沒回應；這不是「沒有資料」，請按重試。',
    },
    unauthorized: {
        title: '登入已失效（401／403）',
        hint: '請重新登入；在重新登入前建立帳號會失敗，不要重複建立。',
    },
    error: {
        title: '讀取失敗',
        hint: '資料沒有載入成功，畫面上看到的不是「空的」。可按重試。',
    },
};
/**
 * `unauthorized` 這一態的**實際**文案：同一種狀態碼底下其實有三件事，別再說成同一句。
 *
 * 舊版一律寫「登入已失效，請重新登入」，於是「缺操作金鑰（X-Api-Token）」的人（他**剛登入**）
 * 被叫去重新登入，重登幾次都不會好。判斷順序與 `api/error-copy.ts` 一致：
 *   1. 訊息帶金鑰字樣 → 需要操作金鑰（去設定貼一次，不是重新登入）
 *   2. 訊息講權限／403     → 這項操作需要管理權限
 *   3. 其餘               → 真的登入過期
 *
 * 只吃 `message` 字串（`DataStateNotice` 手上只有這個）：呼叫端不必為了文案多傳一個 error 物件。
 */
export function dataStateCopyOf(state, message) {
    const base = state === 'ready' ? DATA_STATE_COPY.empty : DATA_STATE_COPY[state];
    if (state !== 'unauthorized')
        return base;
    const text = typeof message === 'string' ? message : '';
    if (isApiTokenMessage(text)) {
        return {
            title: '需要操作金鑰（不是登入問題）',
            hint: '到「操作金鑰」設定貼一次（只存在這個分頁）；貼完按重試即可，不用重新登入。',
        };
    }
    if (isForbiddenMessage(text) || /\b403\b/.test(text)) {
        return {
            title: '這項操作需要管理權限',
            hint: '你的身分沒有這個權限；請管理員開權限或改用管理員帳號，這不是資料是空的。',
        };
    }
    return base;
}
//# sourceMappingURL=data-state.js.map